import { z } from 'zod';
import { flowCfgSchema } from './latest/flow/flow.zod';
import {
    type BaseNodeCfg,
    DataSocketKind,
    NodeKind,
    type NodeSocketKind,
    nodeCfgSchemaMap,
    nodeSocketKindMap,
} from './latest/flow/nodes';
import type { ArgoMaterialStatusEnum } from '../../db';
import type { MaterialComparisonNodeCfgSchema } from './latest/flow/nodes/material-comparison.zod';
import type { processWorkInstruction, workInstructionNodeCfgSchema } from './latest/flow/nodes/work-instruction.zod';
import type { appletDesignVersionConfigSchema_v4 } from './latest/appletDesign.zod';
import type { processCollectNodeCfgSchema } from './latest/flow/nodes/process-collect.zod';
import type { processSolveNodeCfgSchema } from './latest/flow/nodes/process-solve.zod';
import { staticWorkOrderStatusEnumSchema } from './latest/flow/nodes/work-order-set-status.zod';
import type { GoToTabNodeCfgSchema } from './latest/flow/nodes/go-to-tab.zod';
import type { NextTabNodeCfgSchema } from './latest/flow/nodes/next-tab.zod';

export * from './latest/flow/nodes';
export * from './latest/flow/nodes/connection.zod';
export * from './latest/flow/nodes/work-order.node-cfg';

export interface FlowCfg extends z.infer<typeof flowCfgSchema> {}

/** get the type of a node cfg from its kind */
export type NodeCfgMap = {
    [nodeKind in NodeKind]: z.infer<typeof nodeCfgSchemaMap[nodeKind]>;
}

/** all the node cfgs as a union */
export type AnyNodeCfg = NodeCfgMap[keyof NodeCfgMap];

type _NodeControlSocketKindMap = {
    [kind in NodeKind]: {
        // unwraps the return types of the functions
        inputs: ReturnType<typeof nodeSocketKindMap[kind]['controlSockets']['inputs']>,
        outputs: ReturnType<typeof nodeSocketKindMap[kind]['controlSockets']['outputs']>,
    }
};
/** maps a node kind to its control sockets and their kinds */
export interface NodeControlSocketKindMap extends _NodeControlSocketKindMap {}

export type _NodeDataSocketKindMap = {
    [kind in NodeKind]: {
        // unwraps the return types of the functions
        inputs: ReturnType<typeof nodeSocketKindMap[kind]['dataSockets']['inputs']>,
        outputs: ReturnType<typeof nodeSocketKindMap[kind]['dataSockets']['outputs']>,
    }
};
/** maps a node kind to its data sockets and their kinds */
export interface NodeDataSocketKindMap extends _NodeDataSocketKindMap {}

type _NodeSocketKindMap = {
    [kind in NodeKind]: {
        inputs: NodeDataSocketKindMap[kind]['inputs'] & NodeControlSocketKindMap[kind]['inputs'],
        outputs: NodeDataSocketKindMap[kind]['outputs'] & NodeControlSocketKindMap[kind]['outputs'],
    }
}
/** maps a node kind to its sockets and their kinds */
export interface NodeSocketKindMap extends _NodeSocketKindMap {}

export type DataSocketValueMap = {
    anyData: unknown;
    number: number;
    string: string;
    varValue: z.infer<typeof appletDesignVersionConfigSchema_v4>['appletVariables'][string]['defaultValue'];
    boolean: boolean;
    workOrderMaterialData: {
        id: string;
        status: ArgoMaterialStatusEnum;
    }[];
};

export const dataSocketDisplayPropertyMap: {
    [key in NodeSocketKind]: {
        title: string;
    }
} = {
    boolean: {
        title: 'Boolean',
    },
    number: {
        title: 'Number',
    },
    string: {
        title: 'String',
    },
    varValue: {
        title: 'Variable Value',
    },
    workOrderMaterialData: {
        title: 'Work Order Material Data\n\nArray of the following objects:\n{\n\tid: String\n\tstatus: "Completed" | "Failed" | "None" | "Passed" | "Quarantined" | "Scrapped" | "Started"\n}',
    },
    event: {
        title: 'Event',
    },
    anyData: {
        title: 'AnyData',
    },
};

/** this type is here to error if we add a data socket kind and not specify a value mapping in `DataSocketValueMap` */
export type DataSocketValue = DataSocketValueMap[DataSocketKind];


export type ProcessWorkInstruction = z.infer<typeof processWorkInstruction>;
export type LatestMaterialComparisonNodeCfgSchema = MaterialComparisonNodeCfgSchema;
export type LatestMaterialComparisonCfg = LatestMaterialComparisonNodeCfgSchema['controlValues']['comparisonCfg'];
export type LatestWorkInstructionNodeCfgSchema = z.infer<typeof workInstructionNodeCfgSchema>;
export type LatestProcessCollectNodeCfgSchema = z.infer<typeof processCollectNodeCfgSchema>;
export type LatestProcessCollectStandardizedOutput = LatestProcessCollectNodeCfgSchema['controlValues']['useStandardizedOutputs'];
export type LatestProcessSolveNodeCfgSchema = z.infer<typeof processSolveNodeCfgSchema>;
export type LatestProcessSolveStandardizedOutput = LatestProcessSolveNodeCfgSchema['controlValues']['useStandardizedOutputs'];
export type LatestBaseNodeCfg = BaseNodeCfg;
export type LatestStaticWorkOrderStatusEnum = z.infer<typeof staticWorkOrderStatusEnumSchema>;
export type LatestGoToTabNodeCfgSchema = GoToTabNodeCfgSchema;
export type LatestNextTabNodeCfgSchema = NextTabNodeCfgSchema;
